import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, ShieldCheck, Clock, Globe2, Activity, CheckCircle2, Star, Phone, Video, Zap, BadgeIndianRupee, MessageCircle as MessageCircleIcon } from "lucide-react";
import PublicLayout from "../components/PublicLayout";
import VideoConsultModal from "../components/VideoConsultModal";
import LeaderboardSection from "../components/LeaderboardSection";
import FounderTrustStrip from "../components/FounderTrustStrip";
import FounderOfficeHours from "../components/FounderOfficeHours";
import FormHubSection from "../components/FormHubSection";
import LatestJobsColumn from "../components/LatestJobsColumn";
import LatestSchemesColumn from "../components/LatestSchemesColumn";
import Seo from "../components/Seo";
import { SERVICES } from "../data/services";

const STATS = [
  { value: "99.99%", label: "Uptime SLA" },
  { value: "24/7", label: "NOC Support" },
  { value: "350+", label: "Servers Managed" },
  { value: "500+", label: "Happy Clients" },
];

const WHY = [
  { icon: ShieldCheck, title: "Enterprise-grade security", text: "ISO-aligned processes, encrypted backups and 24×7 monitoring across every endpoint we manage." },
  { icon: Clock, title: "True 24×7 response", text: "Real engineers on call, not bots. P1 incident response under 15 minutes." },
  { icon: Globe2, title: "Multi-cloud expertise", text: "AWS, Azure, GCP and bare-metal — we architect, migrate and run the stack you need." },
  { icon: Activity, title: "Measurable outcomes", text: "Service reviews, uptime reports, SEO dashboards. You see exactly what you're paying for." },
];

const TESTIMONIALS = [
  { name: "Rohit Sharma", role: "CTO, Finlytics", text: "UID Infotech migrated us to a multi-region setup with zero downtime. The NOC team is unreal — they spotted issues before we did." },
  { name: "Priya Verma", role: "Founder, NestKart", text: "From the web build to SEO, they doubled our organic traffic in 6 months. Truly a one-stop shop." },
  { name: "Anand Iyer", role: "IT Head, MedixCare", text: "Their on-site team turned around 30 laptops in 48 hours. We trust them with our entire fleet now." },
];

export default function Home() {
  const { t } = useTranslation();
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <PublicLayout>
      <Seo title="Enterprise IT, Cloud & Engineering Services" path="/" />

      {/* ABOVE-THE-FOLD — MPSC-style 3-column dashboard.
          Desktop: [Latest Jobs] [Form Hub CTA] [Latest Govt Schemes]
          Mobile:  Center → Left → Right (order utilities). */}
      <section className="relative overflow-hidden bg-radial-glow" data-testid="home-dashboard-3col">
        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
        <div className="container-x relative pt-8 pb-10 lg:pt-14 lg:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5 items-stretch">

            {/* LEFT COLUMN — Latest Jobs (Static) */}
            <div className="order-2 lg:order-1 lg:col-span-3 animate-fade-up" data-testid="col-latest-jobs">
              <LatestJobsColumn />
            </div>

            {/* CENTER COLUMN — Form Hub CTA (Hero) */}
            <div className="order-1 lg:order-2 lg:col-span-6 animate-fade-up" data-testid="col-form-hub-cta">
              <div className="relative h-full min-h-[520px] rounded-2xl border border-emerald-400/25 bg-gradient-to-br from-emerald-500/[0.10] via-teal-500/[0.06] to-cyan-500/[0.10] backdrop-blur-md p-6 sm:p-8 overflow-hidden flex flex-col">
                <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-emerald-400/20 blur-3xl pointer-events-none" aria-hidden="true" />
                <div className="absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-cyan-400/15 blur-3xl pointer-events-none" aria-hidden="true" />

                <span className="relative eyebrow text-emerald-300" data-testid="hero-form-hub-eyebrow">
                  {t("form_hub.eyebrow")}
                </span>

                <h1 className="relative mt-4 text-3xl sm:text-4xl lg:text-[2.6rem] font-extrabold tracking-tight leading-[1.08]">
                  {t("form_hub.title_a")}{" "}
                  <span className="bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300 bg-clip-text text-transparent">
                    {t("form_hub.title_b")}
                  </span>
                </h1>

                <p className="relative mt-4 text-sm sm:text-base text-white/75 leading-relaxed max-w-xl">
                  {t("form_hub.subtitle")}
                </p>

                <div className="relative mt-5 flex flex-wrap gap-2 text-[11px]">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2.5 py-1 text-emerald-300 font-semibold">
                    <ShieldCheck className="h-3.5 w-3.5" /> {t("form_hub.badges.humans")}
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-2.5 py-1 text-cyan-300 font-semibold">
                    <Clock className="h-3.5 w-3.5" /> {t("form_hub.badges.turnaround")}
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-500/10 px-2.5 py-1 text-amber-300 font-semibold">
                    <BadgeIndianRupee className="h-3.5 w-3.5" /> {t("form_hub.badges.fixed_price")}
                  </span>
                </div>

                <div className="relative mt-auto pt-6 flex flex-col sm:flex-row gap-3">
                  <a
                    href="#form-hub"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#20bf5c] px-5 py-3.5 text-sm font-bold text-white shadow-[0_10px_28px_-10px_rgba(37,211,102,0.65)] transition-all"
                    data-testid="hero-formhub-cta"
                  >
                    <MessageCircleIcon className="h-4 w-4" /> {t("common.send_documents_wa")}
                    <ArrowRight className="h-4 w-4" />
                  </a>

                  <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1.5 text-[11px] font-bold text-amber-200 self-center"
                       data-testid="hero-formhub-pricing">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-300 animate-pulse" />
                    {t("form_hub.pricing_note")}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN — Latest Govt Schemes (Static) */}
            <div className="order-3 lg:order-3 lg:col-span-3 animate-fade-up" data-testid="col-latest-schemes">
              <LatestSchemesColumn />
            </div>
          </div>
        </div>
      </section>

      {/* IT BACKBONE — restored enterprise IT positioning, sits above founder strip */}
      <section className="relative border-t border-white/5" data-testid="it-backbone-section">
        <div className="container-x py-10 sm:py-14 lg:py-16">
          <div className="max-w-3xl">
            <span className="eyebrow" data-testid="it-backbone-eyebrow">{t("hero.eyebrow")}</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]" data-testid="it-backbone-heading">
              {t("hero.title_a")}{" "}
              <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-emerald-300 bg-clip-text text-transparent">
                {t("hero.title_b")}
              </span>
            </h2>
            <p className="mt-5 max-w-2xl text-base text-white/70 leading-relaxed" data-testid="it-backbone-subtitle">
              {t("hero.subtitle")}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/request" className="btn-primary" data-testid="it-backbone-request-btn">
                {t("hero.cta_primary")} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/services/server-management" className="btn-secondary" data-testid="it-backbone-services-btn">
                {t("hero.cta_secondary")}
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/60">
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> {t("hero.trust.uptime")}</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> {t("hero.trust.iso")}</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> {t("hero.trust.sla")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER TRUST STRIP — sits between hero and video CTA */}
      <section className="relative -mt-4 sm:-mt-6 mb-4">
        <FounderTrustStrip />
      </section>

      {/* FOUNDER OFFICE HOURS — book a free 15-min Zoom-style call */}
      <FounderOfficeHours />

      {/* ONLINE FORM FILLING HUB — WhatsApp-first PAN / Passport / Udyam etc. */}
      <FormHubSection />

      {/* INSTANT VIDEO CTA BANNER */}
      <section className="relative mt-6">
        <div className="container-x">
          <button
            type="button"
            onClick={() => setVideoOpen(true)}
            className="group relative w-full overflow-hidden rounded-2xl border border-emerald-500/30 bg-gradient-to-r from-emerald-500/15 via-[#0F172A] to-blue-600/15 p-5 sm:p-6 text-left transition-all hover:border-emerald-400/60"
            data-testid="hero-video-cta"
          >
            <div className="absolute -top-12 -right-12 h-44 w-44 rounded-full bg-emerald-500/25 blur-3xl pointer-events-none" />
            <div className="relative flex flex-col lg:flex-row lg:items-center gap-5">
              <div className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-400 text-[#0A101D] shadow-lg shadow-emerald-500/30">
                <Video className="h-7 w-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-emerald-300">
                    <Zap className="h-3 w-3" /> New · Premium
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] text-white/50">Pay · Connect · Solved</span>
                </div>
                <h3 className="mt-2 text-xl sm:text-2xl font-bold leading-tight">
                  Instant Expert Video Call Support — from <span className="text-emerald-300">₹200</span> <span className="text-amber-300 text-base font-semibold">· Premium ₹399</span>
                </h3>
                <p className="mt-1 text-sm text-white/65 max-w-2xl">
                  Get your IT problem solved live, on a private video call with a certified engineer. Pick any engineer (₹200) or request a top-rated Premium expert (₹399). Money-back if we can&apos;t fix it.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="hidden sm:block text-right">
                  <div className="text-3xl font-extrabold stat-num leading-none">₹200</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1">flat fee</div>
                </div>
                <span className="btn-primary !bg-emerald-500 hover:!bg-emerald-600 group-hover:gap-3 transition-all" data-testid="hero-video-cta-btn">
                  Pay & Get Help <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </div>
          </button>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-pad border-t border-white/5">
        <div className="container-x">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <span className="eyebrow">What we do</span>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold">One partner. Every layer of your IT.</h2>
            </div>
            <p className="max-w-md text-white/60">Five practice areas, one accountable team. Built so you never have to chase a vendor again.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* Premium Instant Video Card — first in grid */}
            <button
              type="button"
              onClick={() => setVideoOpen(true)}
              className="card-surface group flex flex-col gap-4 text-left relative overflow-hidden border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-[#0F172A] to-[#111827]"
              data-testid="home-service-video-consultation"
            >
              <span className="absolute top-4 right-4 rounded-full bg-emerald-500/15 border border-emerald-500/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-300">From ₹200</span>
              <div className="grid h-12 w-12 place-items-center rounded-lg bg-gradient-to-br from-emerald-400 to-cyan-400 text-[#0A101D]">
                <Video className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold">Instant Expert Video Call</h3>
              <p className="text-sm text-white/60">Pay ₹200 for any available engineer or ₹399 for a top-rated Premium expert — both connect within minutes.</p>
              <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-300 group-hover:gap-2.5 transition-all">
                <BadgeIndianRupee className="h-4 w-4" /> Pay & Connect Now <ArrowRight className="h-4 w-4" />
              </span>
            </button>

            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="card-surface group flex flex-col gap-4"
                  data-testid={`home-service-${s.slug}`}
                >
                  <div className={`grid h-12 w-12 place-items-center rounded-lg bg-gradient-to-br ${s.color} text-[#0A101D]`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-semibold">{s.title}</h3>
                  <p className="text-sm text-white/60">{s.short}</p>
                  <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-blue-400 group-hover:gap-2.5 transition-all">
                    Learn more <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-white/5 bg-gradient-to-b from-[#0B1220] to-[#0A101D]">
        <div className="container-x py-16 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-4xl sm:text-5xl font-bold stat-num">{s.value}</div>
              <div className="mt-2 text-sm uppercase tracking-wider text-white/50">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY */}
      <section className="section-pad">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="eyebrow">Why UID Infotech</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold">Engineering rigor. Operator empathy.</h2>
            <p className="mt-5 text-white/65 leading-relaxed">
              We blend deep infrastructure expertise with hands-on operator culture. The result: less downtime, faster shipping, and a single accountable partner across your entire tech stack.
            </p>
            <Link to="/about" className="mt-7 inline-flex items-center gap-2 text-blue-400 font-semibold hover:gap-3 transition-all" data-testid="why-about-link">
              Learn how we work <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {WHY.map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.title} className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
                  <Icon className="h-7 w-7 text-blue-400" />
                  <h4 className="mt-3 font-semibold text-lg">{w.title}</h4>
                  <p className="mt-1.5 text-sm text-white/60">{w.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-pad border-t border-white/5 bg-[#0B1220]">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto">
            <span className="eyebrow">Customer love</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold">Trusted by teams that ship 24×7.</h2>
          </div>
          <div className="mt-12 grid lg:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="rounded-xl border border-white/10 bg-[#111827] p-6 flex flex-col gap-4" data-testid={`testimonial-${t.name.split(' ')[0].toLowerCase()}`}>
                <div className="flex gap-0.5 text-amber-400">
                  {["s1", "s2", "s3", "s4", "s5"].map((k) => <Star key={`${t.name}-${k}`} className="h-4 w-4 fill-amber-400" />)}
                </div>
                <p className="text-white/80 leading-relaxed">&ldquo;{t.text}&rdquo;</p>
                <div className="mt-auto">
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-xs text-white/50">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PUBLIC LEADERBOARD — live trust signals */}
      <LeaderboardSection />

      {/* CTA */}
      <section className="section-pad">
        <div className="container-x">
          <div className="relative overflow-hidden rounded-3xl border border-blue-500/20 bg-gradient-to-br from-blue-600/15 via-[#0F172A] to-[#0A101D] p-10 lg:p-16">
            <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />
            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold">Ready to talk to a real engineer?</h2>
                <p className="mt-4 text-white/70 max-w-md">Book a free consultation — we&apos;ll audit your current stack and propose a roadmap within 48 hours.</p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Link to="/request" className="btn-primary" data-testid="cta-request-btn">
                  Request a Quote <ArrowRight className="h-4 w-4" />
                </Link>
                <a href="tel:+918055592955" className="btn-secondary" data-testid="cta-call-btn">
                  <Phone className="h-4 w-4" /> Call 24/7
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <VideoConsultModal open={videoOpen} onClose={() => setVideoOpen(false)} />
    </PublicLayout>
  );
}
